import type { BaseDocument } from './mongo/base-document.interface';
import type { HandlingUnitType } from './handling-unit-type/handling-unit-type.interface';
import type { Location } from './geolocation';
import type { PhoneNumber } from './phone';
import type { SiteType } from './site/site.interface';
import type { SyncResource } from './sync/sync-resource';
import type { UserSummary } from './user/user.interface';
import type { Vehicle } from './vehicle/vehicle.interface';
import type { VehicleTemplate } from './vehicle-template/vehicle-template.interface';

/**
 * Supported country codes (ISO 3166-1 alpha-2).
 * To add a new country, append its code here.
 */
export const SUPPORTED_COUNTRIES = ['FR', 'LB'] as const;

/**
 * Type derived from the supported countries array.
 */
export type SupportedCountry = (typeof SUPPORTED_COUNTRIES)[number];

/**
 * Tenant status values shared across all apps.
 */
export const TENANT_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DRAFT: 'draft',
  INACTIVE: 'inactive',
  INITIALIZATION_FAILED: 'initialization_failed',
  INITIALIZING: 'initializing',
} as const;

export type TenantStatus = (typeof TENANT_STATUS)[keyof typeof TENANT_STATUS];

/**
 * Tenant activity type (business domain the tenant operates in).
 */
export const TENANT_ACTIVITY_TYPE = {
  BULKY_ITEM_DELIVERY: 'bulky-item-delivery',
  FOOD_GROCERY_DELIVERY: 'food-grocery-delivery',
  OTHER: 'other',
  PARCEL_EXPRESS_DELIVERY: 'parcel-express-delivery',
} as const;

export type TenantActivityType = (typeof TENANT_ACTIVITY_TYPE)[keyof typeof TENANT_ACTIVITY_TYPE];

/**
 * User-facing tenant status values for filtering.
 * Excludes Draft and transient system states (Initializing, InitializationFailed).
 */
export const TENANT_STATUSES: readonly TenantStatus[] = [
  TENANT_STATUS.ACTIVE,
  TENANT_STATUS.ARCHIVED,
  TENANT_STATUS.INACTIVE,
] as const;

/**
 * Tenant segment (business type).
 */
export const TENANT_SEGMENT = {
  DELIVERY_SERVICE_PARTNER: 'delivery-service-partner',
} as const;

export type TenantSegment = (typeof TENANT_SEGMENT)[keyof typeof TENANT_SEGMENT];

/**
 * Supported language codes (i18next-compatible).
 */
export type TenantLanguage = 'en' | 'fr';

/**
 * Main contact information embedded in a Tenant.
 *
 * `userId` is the canonical Tenant Owner reference.
 * The first tenant admin created during initialization is automatically linked here,
 * making them the initial owner. Ownership can be transferred via the transfer-ownership
 * endpoint, which atomically repoints `userId` and overwrites the name/email/phone fields
 * with the new owner's data.
 */
export interface TenantMainContact {
  email: string;
  firstName: string;
  lastName: string;
  phone?: PhoneNumber;
  userId?: string;
}

/**
 * `TenantMainContact` without `userId` — the client-facing shape accepted by create/update
 * payloads. `userId` is system-managed (set during initialization or ownership transfer) and
 * must never be accepted from a request body.
 */
export type TenantMainContactInput = Omit<TenantMainContact, 'userId'>;

/**
 * First Space/Site to auto-provision for a `delivery-service-partner` tenant at initialization.
 * Optional: legacy tenants without it skip provisioning and initialize as before.
 */
export interface TenantInitialSite {
  code: string;
  location: Location;
  name: string;
  phone?: PhoneNumber;
  type?: SiteType;
}

/**
 * Full Tenant entity as stored in MongoDB.
 * A tenant's tenantId is set to its own _id (self-referencing).
 */
export interface Tenant extends BaseDocument {
  activityType?: TenantActivityType;
  code: string;
  country: SupportedCountry;
  initializedAt: Date | null;
  initialSite?: TenantInitialSite;
  language: TenantLanguage;
  mainContact: TenantMainContact;
  name: string;
  onboardingWizardCompleted?: boolean;
  segment: TenantSegment;
  status: TenantStatus;
}

/**
 * Payload for creating a new Tenant.
 * Omits BaseDocument fields (id, tenantId, timestamps) + code (auto-generated) + status (always 'inactive') +
 * initializedAt (system-managed) + activityType (arrives via the onboarding wizard, self-service only, post-creation).
 * `mainContact` is narrowed to `TenantMainContactInput` — `userId` is never client-settable.
 */
export type CreateTenant = Omit<
  Tenant,
  | 'activityType'
  | 'code'
  | 'createdAt'
  | 'deletedAt'
  | 'id'
  | 'initializedAt'
  | 'mainContact'
  | 'status'
  | 'tenantId'
  | 'updatedAt'
> & {
  mainContact: TenantMainContactInput;
};

/**
 * Minimal tenant profile returned by GET /tenants/me.
 */
export interface TenantProfile {
  code: string;
  id: string;
  name: string;
  segment: TenantSegment;
}

/**
 * Payload for updating an existing Tenant via PATCH. name/segment/mainContact/initialSite are
 * only editable before initialization; `mainContact.userId` is always preserved, never settable.
 */
export interface UpdateTenant {
  country?: SupportedCountry;
  initialSite?: TenantInitialSite;
  language?: TenantLanguage;
  mainContact?: TenantMainContactInput;
  name?: string;
  onboardingWizardCompleted?: boolean;
  segment?: TenantSegment;
  status?: typeof TENANT_STATUS.ACTIVE | typeof TENANT_STATUS.INACTIVE;
}

/**
 * Ids of the Carrier/Site/Space auto-provisioned at a `delivery-service-partner` tenant's first
 * initialization. Resolved by code (see `TenantService.provisionDspResources`), present only when
 * all three exist.
 */
export interface OnboardingWizardProvisionedResources {
  carrierId: string;
  siteId: string;
  spaceId: string;
}

/**
 * Response of `GET /tenants/me/onboarding-wizard-info`.
 * The pre-fill fields are only populated when `completed` is `false`.
 */
export interface OnboardingWizardInfo {
  activityType?: TenantActivityType;
  completed: boolean;
  defaultHandlingUnitTypeId?: string | null;
  drivers?: UserSummary[];
  handlingUnitTypes?: HandlingUnitType[];
  provisionedResources?: OnboardingWizardProvisionedResources;
  vehicles?: Vehicle[];
  vehicleTemplates?: VehicleTemplate[];
}

// ─── Sync resource descriptor (declaration-merged with the Tenant interface) ─

/**
 * Runtime descriptor for Tenant collection synchronization.
 * Use `@SyncSource(Tenant)` on a service to publish create/update/delete events.
 * Use `SyncTarget<Tenant>` to mirror the collection in a target app.
 */
export const Tenant: SyncResource<Tenant> = {
  collection: 'tenants',
  name: 'tenant',
};
